import { createContext, useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import keikkaLaskuClass from "src/data/keikkaLasku/keikkaLaskuClass";
import { useUserData } from "./userContext";
import { KeikkaDataContext } from "./KeikkaDataContext";
import { keikkaLasku_getAllKeikkaLaskutForKeikka } from "src/data/keikkaLasku/KeikkaLaskuFunctions";

export const KeikkaLaskuContext = createContext();

export const KeikkaLaskuProvider = ({ children }) => {
  const { keikkaData, maksajaAsiakas: maksajaAsiakas_keikka } = useContext(KeikkaDataContext) || {};
  const { token, userAsiakas } = useUserData();

  const [keikkaLasku, setKeikkaLasku] = useState();
  const [keikkaLaskut, setKeikkaLaskut] = useState([]);
  const [uniqueHash, setUniqueHash] = useState();
  const [hasAlennus, setHasAlennus] = useState(false);
  const [isLoaded, setIsLoaded] = useState({ init: false });

  const maksajaAsiakasId = keikkaLasku?.maksajaAsiakasId || maksajaAsiakas_keikka?.asiakasId;
  const laskuttajaAsiakasId = userAsiakas?.asiakasId;
  const keikkaId = keikkaData?.keikkaId;

  const [keikkaLaskuStatus, setKeikkaLaskuStatus] = useState({
    isLoaded: false,
    error: null,
    state: "initial",
  });

  useEffect(() => {
    if (!keikkaId || !laskuttajaAsiakasId || !keikkaLaskuStatus.isLoaded || !maksajaAsiakasId)
      return;
    const asynk = async () => {
      const a = await keikkaLasku_getAllKeikkaLaskutForKeikka(keikkaId, laskuttajaAsiakasId, token);
      let keikkaLaskut = [];
      for (let i = 0; i < a.length; i++) {
        const keikkaLasku = new keikkaLaskuClass(
          keikkaId,
          laskuttajaAsiakasId,
          a[i].maksajaAsiakasId
        );
        const r = await keikkaLasku.init(token);
        if (r === true) keikkaLaskut.push(keikkaLasku);
      }
      if (keikkaLaskut.length > 0) {
        setKeikkaLaskut(keikkaLaskut);
        setKeikkaLasku(keikkaLaskut[0]);
        setUniqueHash(keikkaLaskut[0].uniqueHash);
        setKeikkaLaskuStatus({ isLoaded: true, error: null, state: "success" });
      }
    };
    asynk();
  }, [keikkaId, laskuttajaAsiakasId, keikkaLaskuStatus.isLoaded]);

  useEffect(() => {
    if (!keikkaId || !token || !maksajaAsiakasId) return;
    if (!laskuttajaAsiakasId || !maksajaAsiakasId) {
      setIsLoaded({ ...isLoaded, init: true });
      return;
    }
    const _asynk = async () => {
      const keikkaLasku = await loadKeikkaLasku();
      setHasAlennus(keikkaLasku.keikkaLaskuRivit.some((rivi) => rivi.aleProsentti > 0));
    };
    _asynk();
  }, [keikkaId, token, laskuttajaAsiakasId, maksajaAsiakasId]);

  const loadKeikkaLasku = async () => {
    setIsLoaded({ ...isLoaded, init: false });
    const keikkaLasku = new keikkaLaskuClass(keikkaId, laskuttajaAsiakasId, maksajaAsiakasId);
    const r = await keikkaLasku.init(token);
    if (r === true) {
      doSetKeikkaLasku(keikkaLasku);
      setKeikkaLaskuStatus({ isLoaded: true, error: null, state: "success" });
    } else {
      doSetKeikkaLasku(null);
      setKeikkaLaskuStatus({
        isLoaded: true,
        error: "error initializing keikkaLasku",
        state: "error",
      });
    }
    setIsLoaded({ ...isLoaded, init: true });
    return keikkaLasku;
  };

  const doSetKeikkaLasku = (keikkaLasku) => {
    setKeikkaLasku(keikkaLasku);
    setUniqueHash(keikkaLasku?.uniqueHash);
  };

  const resetLoadingState = () => {
    setKeikkaLaskuStatus({ ...keikkaLaskuStatus, state: "loading" });
    setIsLoaded({ ...isLoaded, init: false });
  };

  const isLoadedPercent = {
    percent: Math.round(
      (Object.values(isLoaded).filter((v) => v).length / Object.keys(isLoaded).length) * 100
    ),
    count: Object.keys(isLoaded).length,
  };

  const reloadKeikkaLasku = async () => {
    return await loadKeikkaLasku();
  };

  return (
    <KeikkaLaskuContext.Provider
      value={{
        keikkaLasku,
        keikkaLaskut,
        doSetKeikkaLasku,
        keikkaLaskuStatus,
        isLoadedPercent,
        isKeikkaLaskuLoaded: keikkaLasku?.isLoaded,
        reloadKeikkaLasku,
        setIsLoading: resetLoadingState,
        resetLoadingState,
        hasAlennus,
      }}>
      {children}
    </KeikkaLaskuContext.Provider>
  );
};

export const useKeikkaLaskuData = () => useContext(KeikkaLaskuContext);

KeikkaLaskuProvider.propTypes = {
  children: PropTypes.node.isRequired,
  keikkaId: PropTypes.number,
};
